/**
 * Intercetores axios — portados do ElevaChecks (apiInterceptors.ts), onde
 * correm em produção há meses.
 *
 * Request: anexa o Bearer do accessToken a todos os pedidos.
 * Response: num 401, renova o token com o refresh e REPETE o pedido original
 * sem o utilizador notar; fila evita renovações duplicadas em pedidos
 * simultâneos. Só faz logout se o próprio refresh falhar.
 */
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import axios, { AxiosHeaders } from 'axios';
import { logout, refreshAccessToken } from '../Backend/services/authService';
import { toastService } from './toastService';
import { lerAccessToken, lerRefreshToken } from '@/security/tokens';
import { CONFIG } from '@/config/environment';
import { SessionRefreshError } from '@/security/refreshSession';

/**
 * O token só vai para o NOSSO backend. Este interceptor está no axios global,
 * e levava o Bearer também para o Cloudinary (upload da foto de perfil), que
 * recusa o header no preflight CORS: a foto "não carregava" desde 2026-08-10.
 */
const paraONossoBackend = (config: AxiosRequestConfig): boolean => {
  try {
    const origem = window.location.origin;
    const base = new URL(config.baseURL || origem, origem);
    const destino = new URL(config.url || '', base);
    const backend = new URL(CONFIG.BACKEND_URL || origem, origem);
    if (!['http:', 'https:'].includes(destino.protocol)) return false;
    return destino.origin === backend.origin || (
      destino.origin === origem &&
      /^\/(api|padel|backend|game_invites|csrf)(\/|$)/.test(destino.pathname)
    );
  } catch {
    return false;
  }
};

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  refreshQueue = [];
};

// Endpoints de autenticação: um 401 aqui é resposta legítima (credenciais
// erradas, refresh expirado) — nunca tentar renovar sobre eles
const AUTH_URLS = [
  '/api/auth/refresh/',
  '/api/auth/login/',
  '/api/auth/google/',
  '/api/auth/logout/',
  '/padel/login/',
  '/padel/register/',
];

export const setupInterceptors = (api: AxiosInstance = axios): void => {
  // Request — Authorization automático (não pisa headers já definidos)
  api.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const token =
        lerAccessToken();
      if (token && !config.headers?.Authorization && paraONossoBackend(config)) {
        if (!config.headers) config.headers = new AxiosHeaders();
        (config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Response — 401 → refresh → retry
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      const url = originalRequest?.url || '';

      if (
        error.response?.status === 401 &&
        originalRequest &&
        paraONossoBackend(originalRequest) &&
        !originalRequest._retry &&
        !AUTH_URLS.some((u) => url.includes(u)) &&
        lerRefreshToken()
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve: (token: string) => {
                if (!originalRequest.headers) originalRequest.headers = new AxiosHeaders();
                (originalRequest.headers as AxiosHeaders).set(
                  'Authorization',
                  `Bearer ${token}`
                );
                resolve(api(originalRequest as AxiosRequestConfig));
              },
              reject,
            });
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            processQueue(null, newToken);
            if (!originalRequest.headers) originalRequest.headers = new AxiosHeaders();
            (originalRequest.headers as AxiosHeaders).set(
              'Authorization',
              `Bearer ${newToken}`
            );
            return api(originalRequest as AxiosRequestConfig);
          }

          processQueue(new Error('Refresh failed'), null);
          toastService.warning('Sessão expirada. Por favor, inicia sessão novamente.');
          logout();
        } catch (refreshError) {
          processQueue(refreshError, null);
          if (refreshError instanceof SessionRefreshError && refreshError.expired) {
            toastService.warning('Sessão expirada. Por favor, inicia sessão novamente.');
            void logout();
          }
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
